import { Values } from "../context";
import Api from "@api";
import { Draft } from "../../../api/drafts";
import { DATE_FORMAT } from "@constants";
import parse from "date-fns/parse";

function parseDate(date: string): Date {
  const [value] = date.split("T");

  return parse(value, DATE_FORMAT, new Date());
}

function getDate(
  effectiveDate: Draft["requestHeader"]["effectiveDate"]
): Values["date"] {
  if (effectiveDate) {
    return parseDate(effectiveDate);
  }
}

function getJustification(
  justification: Draft["requestHeader"]["justification"]
): Values["justification"] {
  return justification;
}

function getDraftKey(id: Draft["id"]): Values["draft_key"] {
  if (id !== undefined) {
    return id.toString();
  }
}

// [WIP]
async function getSystems(
  appList: Draft["appList"],
  appValues: Draft["appValues"]
): Promise<Values["systems"]> {
  return Api.applications.getApplications().then((applications) => {
    return appValues.reduce<Values["systems"]>((systems, { id, appName }) => {
      const application = applications.find(
        (application) => application.id === id && application.name === appName
      );

      return application
        ? systems.concat({
            accesses: appList
              .filter(
                (app) =>
                  (app.appId === application.id &&
                    app.appType === "Infrastructure" &&
                    application.manual &&
                    !application.lz) ||
                  (app.appId === application.id &&
                    !application.manual &&
                    !application.lz) ||
                  (app.appId === application.id &&
                    app.appType === "GCP Landing Zone" &&
                    application.lz &&
                    !application.manual)
              )
              .map<Values["systems"][0]["accesses"][0]>((app) => {
                // Parse the details field to extract accessDetails and form data
                let accessDetails = app.details;
                let formData = undefined;

                try {
                  const parsedDetails = JSON.parse(app.details);
                  if (
                    typeof parsedDetails === "object" &&
                    parsedDetails !== null
                  ) {
                    accessDetails = parsedDetails.accessDetails || app.details;
                    formData = parsedDetails.additionalFormData;
                  }
                } catch (e) {
                  // If parsing fails, treat as plain string (backward compatibility)
                  accessDetails = app.details;
                }

                return {
                  accessDetails,
                  environment: {
                    id: app.environmentId,
                    name: app.environment,
                    isActive: true,
                  },
                  form: formData,
                  infrastructureApplication: application.manual
                    ? {
                        id: app.infraAppId,
                        name: "u",
                      }
                    : undefined,
                  role: {
                    id: app.roleId,
                    name: app.role,
                  },
                  subCategoryName: application.manual ? app.device : undefined,
                };
              }),
            application,
          })
        : systems;
    }, []);
  });
}

function getType(type: Draft["requestHeader"]["requestType"]): Values["type"] {
  switch (type) {
    case 1:
      return "new";
    case 2:
      return "modify";
    case 3:
      return "revoke";
  }
}

function getUsers(userList: Draft["userList"]): Values["users"] {
  return userList.map((user) => ({
    email: user.emailAddress,
    id: user.networkId,
    name: user.name,
    start_date: user.startDate ? parseDate(user.startDate) : undefined,
  }));
}

function getRevokeType(): Values["revoke_type"] {
  return undefined; // [TODO]
}

function getTarget(
  draftRequestForself: Draft["requestHeader"]["draftRequestForself"]
): Values["target"] {
  return draftRequestForself ? "SELF" : "MULTIPLE";
}

export async function getRequestContextValuesFromDraft(
  draft?: Draft
): Promise<Partial<Values> | undefined> {
  return draft
    ? {
        date: getDate(draft.requestHeader.effectiveDate),
        justification: getJustification(draft.requestHeader.justification),
        draft_key: getDraftKey(draft.id),
        systems: await getSystems(draft.appList, draft.appValues), // [TODO]
        type: getType(draft.requestHeader.requestType),
        users: getUsers(draft.userList),
        revoke_type: getRevokeType(), // [TODO] This is stored by user in draft, by is a payload value in request...
        target: getTarget(draft.requestHeader.draftRequestForself),
      }
    : undefined;
}
